import React, { useEffect, useState } from "react";
import DynamicForm from "../../stories/Forms/DynamicForm";
import { resetPassword } from "../../stories/Forms/fields";
import { resetPassSchema } from "../../stories/Forms/validationSchema";
import { XSquare } from "react-bootstrap-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEnvelope } from "@fortawesome/free-solid-svg-icons";
import useApis from "../../services/useAPI";
import { useAuth } from "../../components/AuthContext/AuthContext";
import { resetPasswordUrl } from "../../utils/constants";
import AccountCard from "../../stories/cards/AccountCard";
import PropTypes from "prop-types";
/**
 * ResetPassword Component
 *
 * This component provides a form for users to request a password reset.
 * It handles form submission, API integration for sending reset emails, 
 * and displays success or error messages via toast notifications.
 * 
 * @returns {JSX.Element} The rendered ResetPassword component.
 */

  const ResetPasswordForm = ({handleReset,serverError,doneEmail,loading}) => {
    return (
      <div className="container-fluid reset-password-form px-0">
        <div className="row justify-content-center">
          <div className="col-md-12">
            {doneEmail ? (
              <>
                <p className="text-muted">
                  An email has been sent to{" "}
                  <span className="text-email">{doneEmail}</span> with instructions on resetting your password.
                </p>
                <p className="text-center">You can safely close this window.</p>
              </>
            ) : (
              <>

                <p className="reset-subtitle-center">
                  If you've forgotten your password, enter your email address below. We’ll send you an email with a link to reset your password.
                </p>
                <h3 className="fs-20 ageo-bold text-teal">Enter your email address to begin.</h3>
                <DynamicForm
                  fields={resetPassword}
                  onSubmit={handleReset}
                  validationSchema={resetPassSchema}
                />
                {loading && <p>Submitting...</p>}
              </>
            )}
          </div>
        </div>
      </div>
    )
  }

ResetPasswordForm.propTypes = {
  handleReset: PropTypes.func.isRequired,
  serverError: PropTypes.string,
  doneEmail: PropTypes.string,
  loading: PropTypes.bool.isRequired,
};

const ResetPassword = () => {
  const { callApi, loading, serverError, resultPost } = useApis();
  const auth = useAuth();
  const [doneEmail, setDoneEmail] = useState(null);
  const [submittedEmail, setSubmittedEmail] = useState(null);
  const [toastShown, setToastShown] = useState(false);
  useEffect(() => {
    localStorage.setItem("isLoggedIn", false);
  });


  // Shows toast notification on server error
  useEffect(() => {
    if (serverError && !toastShown) {
      auth?.showToast("Error", serverError, "../assets/mhc.png", <XSquare />);
      setToastShown(true);
    }
  }, [serverError, toastShown, auth]);

  // Handles form submission for password reset
  const handleReset = async values => {
    const payload = {
      email: values.email,
      recaptcha_token: values.recaptcha_token
    };

    // Resets toast control state before making API call
    setToastShown(false);

    setSubmittedEmail(values.email); // Set the submitted email
    await callApi(payload, resetPasswordUrl, "POST");
  };

  useEffect(() => {
    if (resultPost?.data && !toastShown) {
      setDoneEmail(submittedEmail); // Use the submitted email state
      auth?.showToast(
        "Email Sent",
        `Email sent to ${submittedEmail}`, // Use the submitted email state
        "../assets/mhc.png",
        <FontAwesomeIcon icon={faEnvelope} />
      );
      setToastShown(true);
    }
  }, [resultPost, submittedEmail, toastShown, auth]);

  return (
    <AccountCard
      title="Reset password" resetPasswordPage={true} >
      <ResetPasswordForm
        handleReset={handleReset}
        serverError={serverError}
        doneEmail={doneEmail}
        loading={loading}
      />
    </AccountCard>
  );
};

export default ResetPassword;
